import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, the installments CTA close-on-click, and the size-check notice's
 * slide-down / auto-dismiss.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #noticeTimer = null;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    if (this.#noticeTimer) clearTimeout(this.#noticeTimer);
  }

  /**
   * Handles the theme-drawer opening — updates sticky state, wires up the installments CTA,
   * and (re)plays the size-check notice slide-in/auto-dismiss.
   *
   * PERF: both #updateStickyState() and #playNotice() used to force a
   * synchronous layout read/recalc (getBoundingClientRect x2 + an
   * offsetWidth read) right as <theme-drawer> starts its own open
   * animation. When the summary occupies a large share of the drawer's
   * height (few items + coupon/free-shipping rows, e.g. "cart-summary-
   * sticky" flips to false), that pile-up of forced reflows landed mid-
   * animation and showed up as an intermittent stutter/freeze on open.
   * Deferred to a single rAF below so it runs after the browser's own
   * paint step instead of forcing one synchronously mid-script.
   */
  #handleDrawerOpen = () => {
    requestAnimationFrame(() => {
      this.#updateStickyState();
      this.#playNotice();
    });

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * Aviso "Atenção! confira o tamanho": entra deslizando de cima (gaveta
   * vertical) toda vez que o carrinho abre, fica 3s visível e sai deslizando
   * de volta pra cima sozinho.
   *
   * Tudo é controlado por JS (troca de classe) em vez de só CSS puro com
   * animation-delay: dentro de um <dialog> aberto via showModal() (top
   * layer), depender do navegador reiniciar uma animação CSS sozinho a
   * cada display:none->visível não é confiável entre navegadores/versões.
   * Disparado pelo mesmo evento ("theme-drawer:open") que já governa o
   * resto do estado do drawer, então sempre reinicia do zero a cada
   * abertura, mesmo que o carrinho já estivesse aberto antes.
   *
   * PERF: reinicia a transição com um duplo requestAnimationFrame em vez
   * de ler notice.offsetWidth (que forçava um recálculo de layout SÍNCRONO
   * bem no meio da animação de abertura da gaveta — a causa do travamento
   * intermitente). Dois rAF seguidos garantem que o navegador já pintou o
   * estado "sem a classe" antes de reaplicá-la, sem forçar reflow manual.
   */
  #playNotice() {
    const notice = this.querySelector('.touge-cart-notice');
    if (this.#noticeTimer) {
      clearTimeout(this.#noticeTimer);
      this.#noticeTimer = null;
    }
    if (!notice) return;

    notice.classList.remove('touge-cart-notice--visible');

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        notice.classList.add('touge-cart-notice--visible');
      });
    });

    this.#noticeTimer = setTimeout(() => {
      notice.classList.remove('touge-cart-notice--visible');
      this.#noticeTimer = null;
    }, 3000);
  }

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    // Abre o drawer JÁ NO CLIQUE, sem esperar a resposta do /cart/add.js
    // (que pode levar 1-2s de rede) nem o re-render do conteúdo. Antes, se
    // o carrinho estivesse vazio, o drawer só abria depois da resposta
    // completa chegar — na prática o usuário ficava ~2s sem nenhum feedback
    // visual ao clicar "Adicionar ao carrinho". Agora o drawer sempre abre
    // na hora (mostrando o estado atual, vazio ou não) e o conteúdo é
    // atualizado por cima assim que a resposta chega — a animação
    // "vazio → cheio" já existe em cart-items-component.js
    // (startViewTransition com 'fill-cart-drawer'), só não rodava porque o
    // drawer nunca chegava a abrir a tempo de mostrá-la.
    if (shouldAutoOpen && !sourceModal) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = () => requestAnimationFrame(() => this.#updateStickyState());

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
